import { cn } from '@/lib/utils';
import { ArrowUpRight } from 'lucide-react';
import { forwardRef, useRef } from 'react';
import H4 from '../ui/H4';
import H6 from '../ui/H6';

export interface IAccordion {
    title: string;
    text: string;
    index?: number;
    isOpen?: boolean;
    // eslint-disable-next-line no-unused-vars
    toggleAccordion?: (index: number) => void;
    className?: string;
}

const Accordion = forwardRef<HTMLDivElement, IAccordion>(function Accordion(
    { title, text, index = 0, isOpen, toggleAccordion = () => {}, className },
    ref,
) {
    const contentRef = useRef<HTMLDivElement>(null);
    const orderNumber = index + 1;
    const indexLabel = orderNumber < 10 ? `0${orderNumber}` : `${orderNumber}`;

    return (
        <button
            type="button"
            onClick={() => toggleAccordion(index)}
            className={cn('border-b border-dark-grey w-full flex flex-col pb-5 lg:pb-7', className)}
        >
            <div className="flex justify-between items-center w-full text-left focus:outline-none gap-7">
                <H6 className="leading-none">{indexLabel}</H6>
                <div className="flex justify-between items-center w-full">
                    <H4>{title}</H4>
                    <ArrowUpRight
                        className={cn(
                            'transition-transform ease-out duration-300 min-w-[32px]',
                            isOpen && 'rotate-90',
                        )}
                        width={32}
                        height={32}
                        strokeWidth={1}
                    />
                </div>
            </div>
            <div
                ref={ref}
                className="flex items-center w-full overflow-hidden transition-all duration-300 ease-in-out text-left gap-7 leading-tight"
                style={{
                    maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px',
                    opacity: isOpen ? 1 : 0,
                }}
            >
                <H6 className="invisible">{indexLabel}</H6>
                <p className="text-h5 max-lg:text-p pt-5 whitespace-pre-line" ref={contentRef}>
                    {text}
                </p>
            </div>
        </button>
    );
});

export default Accordion;
